(() => {
  const urls = window.btyThemeChunks || {};
  const scripts = new Map();
  let carouselInitialized = false;
  let commerceInitialized = false;
  let searchInitialized = false;
  let replayingInteraction = false;

  const loadScript = (key) => {
    if (scripts.has(key)) return scripts.get(key);

    const src = urls[key];
    if (!src) return Promise.reject(new Error(`Missing theme chunk URL: ${key}`));

    const promise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
          return;
        }

        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.themeChunk = key;
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        resolve();
      }, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

    scripts.set(key, promise);
    return promise;
  };

  const loadCarousel = (root = document) => {
    return loadScript('slider')
      .then(() => loadScript('carousel'))
      .then(() => {
        if (carouselInitialized) return;
        carouselInitialized = true;
        window.btyInitCarousel?.(root);
      });
  };

  const loadCommerce = (root = document) => {
    if (typeof window.btyInitCommerce === 'function') {
      if (!commerceInitialized) {
        commerceInitialized = true;
        window.btyInitCommerce(root);
      }
      return Promise.resolve();
    }

    return loadScript('cart')
      .then(() => loadScript('product'))
      .then(() => {
        if (commerceInitialized) return;
        commerceInitialized = true;
        window.btyInitCommerce?.(root);
      });
  };

  const loadSearch = () => {
    if (typeof window.btyInitSearch === 'function') {
      return loadCommerce().then(() => {
        if (!searchInitialized) {
          searchInitialized = true;
          window.btyInitSearch();
        }
      });
    }

    return loadCommerce()
      .then(() => loadScript('search'))
      .then(() => {
        if (searchInitialized) return;
        searchInitialized = true;
        window.btyInitSearch?.();
      });
  };

  const reinitializeCommerce = (root = document) => {
    const initializeRoot = () => {
      const buttons = Array.from(root.querySelectorAll('.add-to-cart-button'));
      const hasUnboundButton = buttons.some(
        (button) => typeof button.onclick !== 'function'
      );

      if (!commerceInitialized || hasUnboundButton) {
        window.btyInitCommerce?.(root);
      }

      commerceInitialized = true;
    };

    if (typeof window.btyInitCommerce === 'function') {
      initializeRoot();
      return Promise.resolve();
    }

    return loadScript('cart')
      .then(() => loadScript('product'))
      .then(initializeRoot);
  };

  const loadAvailability = (root = document) => {
    if (typeof window.btyCheckProductAvailability === 'function') {
      window.btyCheckProductAvailability(root);
      return Promise.resolve();
    }

    return loadScript('availability').then(() => {
      window.btyCheckProductAvailability?.(root);
    });
  };

  const reportError = (error) => {
    console.error('[theme-loader]', error);
  };

  const whenIdle = (callback) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 2500 });
    } else {
      window.setTimeout(callback, 1);
    }
  };

  const hasCarousel = (root = document, includeSideCart = false) => {
    const carousels = root.querySelectorAll(
      'unity-slider, timeline-carousel, lookbook-sync, .split-slider'
    );

    return Array.from(carousels).some(
      (carousel) => includeSideCart || !carousel.closest('.side-cart')
    );
  };

  const hasCommerce = (root = document) => Boolean(
    root.querySelector('.main-product, #main-cart, .product-card, [data-type="add-to-cart-form"]')
  );

  const hasAvailabilityCards = (root = document) => Boolean(
    root.querySelector('.product-card[data-product-available][data-product-url]')
  );

  const hasSearchPage = (root = document) => Boolean(root.querySelector('main-search'));

  if (hasCarousel()) loadCarousel().catch(reportError);

  if (document.querySelector('.main-product, #main-cart')) {
    loadCommerce().catch(reportError);
  } else if (hasCommerce()) {
    whenIdle(() => loadCommerce().catch(reportError));
  }

  if (hasSearchPage()) whenIdle(() => loadSearch().catch(reportError));

  if (hasAvailabilityCards()) loadAvailability().catch(reportError);

  document.addEventListener('click', (event) => {
    if (replayingInteraction) return;

    const searchTrigger = event.target.closest('.action-search');
    if (searchTrigger && !searchInitialized) {
      event.preventDefault();
      event.stopImmediatePropagation();

      loadSearch()
        .then(() => {
          replayingInteraction = true;
          searchTrigger.click();
          replayingInteraction = false;
        })
        .catch(reportError);
      return;
    }

    const commerceTrigger = event.target.closest(
      '.action-cart, [data-type="add-to-cart-form"] .add-to-cart-button, .product-variants .field-value, .product-variant-tag, .quantity-button'
    );

    if (commerceTrigger && !commerceInitialized) {
      event.preventDefault();
      event.stopImmediatePropagation();

      const dependencies = [loadCommerce()];
      if (commerceTrigger.closest('.action-cart') && hasCarousel(document, true)) {
        dependencies.push(loadCarousel());
      }

      Promise.all(dependencies)
        .then(() => {
          replayingInteraction = true;
          commerceTrigger.click();
          replayingInteraction = false;
        })
        .catch(reportError);
    }
  }, true);

  document.addEventListener('focusin', (event) => {
    if (event.target.closest('.quick-search, main-search') && !searchInitialized) {
      loadSearch().catch(reportError);
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;

    if (hasCarousel(section) && !carouselInitialized) {
      loadCarousel(section).catch(reportError);
    }

    if (hasCommerce(section) && !commerceInitialized) {
      loadCommerce(section).catch(reportError);
    }

    if (hasSearchPage(section) && !searchInitialized) {
      loadSearch().catch(reportError);
    }

    if (hasAvailabilityCards(section)) {
      loadAvailability(section).catch(reportError);
    }
  });

  document.addEventListener('product-card-updated', (event) => {
    const root = event.detail?.root || document;

    if (hasAvailabilityCards(root)) {
      loadAvailability(root).catch(reportError);
    }
  });

  window.btyLoadCarousel = loadCarousel;
  window.btyLoadCommerce = loadCommerce;
  window.btyLoadSearch = loadSearch;
  window.btyReinitCommerce = reinitializeCommerce;
})();
